import { useLocation } from "react-router";
import { css, styled } from "styled-components";

import { UIButton } from "common/components";
import { breakpoints } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTranslate } from "common/utilities/useTranslate";
import { uploadMap } from "domains/createMapDetail/actions";
import { getCanUploadMap, getMapHasUnsavedChanges, getMapIsPending } from "domains/createMapDetail/selectors";
import { activateModal } from "domains/modal/actions";
import { getTmEditMode } from "domains/transformationMap/selectors";

const StyledRow = styled.div<{ editMode: boolean }>`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  width: 100%;
  padding: 0;
  transform: translate3d(0, -100%, 0);
  min-height: 0;
  height: 0;

  ${({ editMode }) => css`
    ${editMode &&
    css`
      min-height: 50px;
      height: auto;
      transform: translate3d(0, 0, 0);
      padding: 1rem;
      @media ${breakpoints.medium} {
        padding: 2rem;
      }
      @media ${breakpoints.large} {
        padding: 0;
        margin-bottom: 1rem;
      }
    `};
  `}
`;

type CreateMapTopBarProps = { setIsSaved: () => void };

export const CreateMapTopBar = ({ setIsSaved }: CreateMapTopBarProps) => {
  const dispatch = useDispatch();
  const go = useGo();
  const { pathname } = useLocation();
  const canSave = useSelector(getCanUploadMap);
  const mapHasUnsavedChanges = useSelector(getMapHasUnsavedChanges);
  const isPending = useSelector(getMapIsPending);
  const editMode = useSelector(getTmEditMode);
  const translate = useTranslate();

  const saveMap = (closeEditor = false) => {
    setIsSaved();
    if (canSave) dispatch(uploadMap({ closeEditor, pathname }));
  };

  const handleSaveClick = () => saveMap();

  const handleSaveAndCloseClick = () => saveMap(true);

  const cancelAction = () => {
    if (mapHasUnsavedChanges) dispatch(activateModal({ id: "CancelMap" }));
    else go("/create");
  };

  return (
    <StyledRow editMode={editMode}>
      <UIButton variant="neutral" onClick={cancelAction} isLoading={isPending}>
        {translate("app.create.create_map.cancel")}
      </UIButton>
      <UIButton
        data-test-id="custom-map-save-button"
        disabled={isPending}
        isLoading={isPending}
        onClick={handleSaveClick}
        variant="filled"
      >
        {isPending ? translate("app.create.create_map.saving") : translate("app.create.create_map.save")}
      </UIButton>
      <UIButton variant="filled" isLoading={isPending} onClick={handleSaveAndCloseClick} disabled={isPending}>
        {isPending ? translate("app.create.create_map.saving") : translate("app.create.create_map.save_and_close")}
      </UIButton>
    </StyledRow>
  );
};
